import React from 'react';
import { ArrowLeftIcon, ScreenFullIcon } from '@primer/octicons-react';
import { LTTooltip } from '@lambdatestincprivate/lt-components';
import { browserDisplayName, formatDurationMs, osDisplayName, relativeTime, toBrowserKey, toOSKey, truncateMiddle } from '../../format';
import { BrowserIcon } from '../../icons/BrowserIcon';
import { CopyLinkButton } from '../../CopyLink/CopyLinkButton';
import { OSIcon } from '../../icons/OSIcon';
import { StatusPill } from './StatusPill';
import { TestListPopover } from './TestListPopover';
import styles from './header.module.css';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
var NOT_IN_PACK = 'Not in evidence packs yet';

/** "Executed on Jul 7, 2026 at 4:17:27 PM" — kaneai's label tooltip. */
function executedTooltip(iso) {
  if (!iso) return undefined;
  var t = Date.parse(iso);
  if (Number.isNaN(t)) return undefined;
  var d = new Date(t);
  var date = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  }).format(d);
  var time = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: true
  }).format(d);
  return "Executed on ".concat(date, " at ").concat(time);
}
function Column(_ref) {
  var label = _ref.label,
    labelTooltip = _ref.labelTooltip,
    children = _ref.children;
  return /*#__PURE__*/_jsxs("div", {
    className: styles.column,
    children: [/*#__PURE__*/_jsx("span", {
      className: styles.columnLabel,
      title: labelTooltip,
      children: label
    }), children]
  });
}
var ConfigDot = function ConfigDot() {
  return /*#__PURE__*/_jsx("span", {
    className: styles.configDot,
    "aria-hidden": "true"
  });
};

/**
 * Port of kaneai's compact TestDetailTopHeader (LTTestHeader compact branch):
 * one row — status pill · labeled columns · actions far right — over an
 * LTDivider. The test name is deliberately absent (kaneai's compact header
 * omits it too); an sr-only h1 keeps the page outline + focus target.
 * Deviation (flagged): a back button leads the row — the standalone viewer
 * has no breadcrumb to navigate back with.
 */
export function CompactHeader(_ref2) {
  var _executedTooltip, _header$executedBy$em;
  var header = _ref2.header,
    titleRef = _ref2.titleRef,
    onBack = _ref2.onBack,
    onEnterFullscreen = _ref2.onEnterFullscreen;
  var os = toOSKey(header.env.os);
  var browser = toBrowserKey(header.env.browser);
  var executed = relativeTime(header.startedAt);
  var configItems = [];
  if (header.env.browser) {
    var _header$env$browserVe;
    configItems.push(/*#__PURE__*/_jsxs("li", {
      title: browserDisplayName(browser),
      children: [/*#__PURE__*/_jsxs("span", {
        className: "ev-sr-only",
        children: [browserDisplayName(browser), ": "]
      }), /*#__PURE__*/_jsx(BrowserIcon, {
        browser: browser,
        size: 16
      }), /*#__PURE__*/_jsx("span", {
        className: styles.columnValue,
        children: (_header$env$browserVe = header.env.browserVersion) !== null && _header$env$browserVe !== void 0 ? _header$env$browserVe : header.env.browser
      })]
    }, "browser"));
  }
  if (header.env.os) {
    configItems.push(/*#__PURE__*/_jsxs("li", {
      title: osDisplayName(os),
      children: [/*#__PURE__*/_jsxs("span", {
        className: "ev-sr-only",
        children: [osDisplayName(os), ": "]
      }), /*#__PURE__*/_jsx(OSIcon, {
        os: os,
        size: 16
      }), /*#__PURE__*/_jsx("span", {
        className: styles.columnValue,
        children: header.env.osVersion ? "".concat(header.env.os, " ").concat(header.env.osVersion) : header.env.os
      })]
    }, "os"));
  }
  if (header.env.resolution) {
    configItems.push(/*#__PURE__*/_jsxs("li", {
      title: "Resolution",
      children: [/*#__PURE__*/_jsx("span", {
        className: "ev-sr-only",
        children: "Resolution: "
      }), /*#__PURE__*/_jsx("span", {
        className: styles.columnValue,
        children: header.env.resolution
      })]
    }, "resolution"));
  }
  return /*#__PURE__*/_jsxs("div", {
    className: styles.headerBlock,
    children: [/*#__PURE__*/_jsx("h1", {
      className: "ev-sr-only",
      tabIndex: -1,
      ref: titleRef,
      children: header.title
    }), /*#__PURE__*/_jsxs("div", {
      className: styles.compactRow,
      children: [/*#__PURE__*/_jsx("button", {
        type: "button",
        className: "".concat(styles.iconButton, " ").concat(styles.iconButtonDefault),
        "aria-label": "Back to all test instances",
        onClick: onBack,
        children: /*#__PURE__*/_jsx(ArrowLeftIcon, {
          size: 16,
          "aria-hidden": "true"
        })
      }), /*#__PURE__*/_jsx(StatusPill, {
        status: header.status
      }), /*#__PURE__*/_jsxs("div", {
        className: styles.columns,
        children: [header.durationMs !== null && /*#__PURE__*/_jsx(Column, {
          label: "Duration",
          children: /*#__PURE__*/_jsx("span", {
            className: styles.columnValue,
            children: formatDurationMs(header.durationMs)
          })
        }), configItems.length > 0 && /*#__PURE__*/_jsx(Column, {
          label: "Configurations",
          children: /*#__PURE__*/_jsx("ul", {
            className: styles.configList,
            children: configItems.map(function (item, i) {
              return /*#__PURE__*/_jsxs(React.Fragment, {
                children: [i > 0 && /*#__PURE__*/_jsx("li", {
                  "aria-hidden": "true",
                  children: /*#__PURE__*/_jsx(ConfigDot, {})
                }), item]
              }, i);
            })
          })
        }), header.testcaseId && /*#__PURE__*/_jsx(Column, {
          label: "Test Case ID",
          children: /*#__PURE__*/_jsx(LTTooltip, {
            text: header.testcaseId,
            direction: "bottom",
            customTriggerStyle: {
              display: 'inline-flex'
            },
            children: header.sharableLink ? /*#__PURE__*/_jsx("a", {
              className: styles.tcLink,
              href: header.sharableLink,
              target: "_blank",
              rel: "noopener noreferrer",
              children: truncateMiddle(header.testcaseId, 15)
            }) : /*#__PURE__*/_jsx("span", {
              className: styles.columnValue,
              children: truncateMiddle(header.testcaseId, 15)
            })
          })
        }), /*#__PURE__*/_jsx(Column, {
          label: "Version",
          labelTooltip: NOT_IN_PACK,
          children: /*#__PURE__*/_jsx("span", {
            className: styles.columnValue,
            title: NOT_IN_PACK,
            children: "\u2014"
          })
        }), /*#__PURE__*/_jsx(Column, {
          label: executed ? "Executed ".concat(executed, " by") : 'Executed by',
          labelTooltip: (_executedTooltip = executedTooltip(header.startedAt)) !== null && _executedTooltip !== void 0 ? _executedTooltip : NOT_IN_PACK,
          children: header.executedBy ? /*#__PURE__*/_jsx("span", {
            className: styles.columnValue,
            title: (_header$executedBy$em = header.executedBy.email) !== null && _header$executedBy$em !== void 0 ? _header$executedBy$em : undefined,
            children: truncateMiddle(header.executedBy.name, 24)
          }) : /*#__PURE__*/_jsx("span", {
            className: styles.columnValue,
            title: NOT_IN_PACK,
            children: "\u2014"
          })
        }), /*#__PURE__*/_jsx(Column, {
          label: "Labels",
          labelTooltip: header.tags.length > 0 ? undefined : NOT_IN_PACK,
          children: header.tags.length > 0 ? /*#__PURE__*/_jsxs("span", {
            className: styles.columnValue,
            title: header.tags.join(', '),
            children: [header.tags.slice(0, 3).join(', '), header.tags.length > 3 ? " +".concat(header.tags.length - 3) : '']
          }) : /*#__PURE__*/_jsx("span", {
            className: styles.columnValue,
            title: NOT_IN_PACK,
            children: "\u2014"
          })
        })]
      }), /*#__PURE__*/_jsxs("div", {
        className: styles.actions,
        children: [/*#__PURE__*/_jsx(CopyLinkButton, {
          className: "".concat(styles.iconButton, " ").concat(styles.iconButtonInvisible)
        }), /*#__PURE__*/_jsx("button", {
          type: "button",
          className: "".concat(styles.iconButton, " ").concat(styles.iconButtonInvisible),
          "aria-label": "Toggle fullscreen",
          onClick: onEnterFullscreen,
          children: /*#__PURE__*/_jsx(ScreenFullIcon, {
            size: 16,
            "aria-hidden": "true"
          })
        }), /*#__PURE__*/_jsx("div", {
          className: styles.actionsDivider,
          "aria-hidden": "true"
        }), /*#__PURE__*/_jsx(TestListPopover, {
          currentId: header.testId
        })]
      })]
    }), /*#__PURE__*/_jsx("div", {
      className: styles.pageDivider,
      "aria-hidden": "true"
    })]
  });
}